import PropTypes from 'prop-types';
import { lazy, Suspense, useState } from 'react';
import {
  ArticleCard,
  BaseModal,
  ModalBleed,
  BounceLoader,
  Carousel,
  Grid,
  GridItem,
} from '@bwoty-web/ui-kit';
import { useGlobals, useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';

import ResortListItem from './listItem/ResortListItem';
import { getGoogleImageUrl } from '../../common/map/googleImageUrl';

import './resortListView.scss';

const MapContent = lazy(() =>
  import(/* webpackChunkName: "map-content" */ '../../common/map/MapContent'),
);

const ResortListView = ({
  initialIsMobile,
  itemContent,
  itemType,
  labels,
  resortList,
}) => {
  const isMobile = useIsMobile(initialIsMobile);
  const isGlobe = useIsGlobetrotter();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const { siteId } = useGlobals();

  let imageSize = '430x215';
  if (!isMobile && isGlobe) imageSize = '288x355';
  else if (isMobile && !isGlobe) imageSize = '330x220';
  else if (!isMobile && !isGlobe) imageSize = '384x250';

  const googleImage = (
    <img
      className="gw-resort-list__map-image"
      alt="map of resort"
      src={getGoogleImageUrl(
        itemContent,
        imageSize,
        isGlobe,
        siteId,
      )}
    />
  );

  const flattenedResorts = resortList
    .map((areaOrResort) => {
      if (!areaOrResort) {
        return null;
      }
      const hasNoUrl = (!areaOrResort.url || areaOrResort.url === '/null') && areaOrResort.resorts;
      if (areaOrResort.id === 'resortsWithoutArea' || hasNoUrl) {
        return areaOrResort.resorts;
      }
      return areaOrResort;
    })
    .flat()
    .filter((resort) => resort !== null);

  const renderMapCard = (columnSpan) => (
    <GridItem columnSpan={columnSpan}>
      <ArticleCard
        buttonVariant="tertiary"
        className="gw-resort-list__map-card"
        image={labels?.mapImage?.media}
        externalImage={isGlobe ? googleImage : undefined}
        imageAspectRatio={isGlobe ? 4 / 5 : 3 / 2}
        imagePosition="top"
        columnSpan={isGlobe ? 3 : 4}
        onClick={() => setIsMapModalOpen(true)}
        onClickElementProps={{ 'aria-haspopup': 'dialog' }}
        title={labels.overview}
        description={labels.mapDescription}
        buttonText={labels.showMap}
      />
    </GridItem>
  );

  const renderResortListItems = (columnSpan) =>
    flattenedResorts.map((areaOrResort) => {
      const resortListItem = (
        <ResortListItem
          key={`resort-${areaOrResort.id}`}
          parentItemType={itemType}
          resort={areaOrResort}
          labels={labels}
          imageAspectRatio={isGlobe ? 4 / 5 : 3 / 2}
        />
      );
      if (isMobile && isGlobe) return resortListItem;
      return (
        <GridItem columnSpan={columnSpan} key={`resort-${areaOrResort.id}`}>
          {resortListItem}
        </GridItem>
      );
    });

  return (
    <div className="gw-resort-list" id="gw-resort-list">
      <BaseModal
        isOpen={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        size="l"
        overlayedCloseButton
      >
        {isMapModalOpen && (
          <Suspense fallback={<BounceLoader />}>
            <ModalBleed>
              <MapContent
                itemContent={{ ...itemContent, itemType }}
                resorts={flattenedResorts}
                labels={labels}
              />
            </ModalBleed>
          </Suspense>
        )}
      </BaseModal>
      {isGlobe ? (
        <Grid disableAutoSizing>
          {renderMapCard(3)}
          <GridItem columnSpan={9}>
            {isMobile ? (
              <Carousel>{renderResortListItems(3)}</Carousel>
            ) : (
              <Grid columns={9} disableAutoSizing>
                {renderResortListItems(3)}
              </Grid>
            )}
          </GridItem>
        </Grid>
      ) : (
        <Grid>
          {renderMapCard(4)}
          {renderResortListItems(4)}
        </Grid>
      )}
    </div>
  );
};

ResortListView.propTypes = {
  resortList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      imageSources: PropTypes.arrayOf(PropTypes.shape({})),
      description: PropTypes.string,
      rating: PropTypes.number,
      numberOfHotels: PropTypes.number,
      url: PropTypes.string,
      isHotNews: PropTypes.bool,
      ratings: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          title: PropTypes.string,
          displayValue: PropTypes.string,
          rating: PropTypes.number,
        }),
      ),
      conceptBanners: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
        }),
      ),
      productInfo: PropTypes.shape({
        isSuitableForTraining: PropTypes.bool,
      }),
    }),
  ).isRequired,
  labels: PropTypes.shape({
    readMoreButtonText: PropTypes.string,
    hotelLinkText: PropTypes.string,
    numberOfResortsLinkText: PropTypes.string,
    isSuitableForTrainingText: PropTypes.string,
    map: PropTypes.string,
    overview: PropTypes.string,
    showMap: PropTypes.string,
    mapDescription: PropTypes.string,
  }).isRequired,
  itemContent: PropTypes.shape({
    wvId: PropTypes.string.isRequired,
    name: PropTypes.string,
    position: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    extent: PropTypes.shape({
      northEast: PropTypes.shape(),
      southWest: PropTypes.shape(),
    }),
  }).isRequired,
  itemType: PropTypes.string.isRequired,
  initialIsMobile: PropTypes.bool.isRequired,
};

export default ResortListView;
